// src/contexts/PlaybookCitationTooltipContext.tsx

import React, { createContext, useContext, useState, useCallback, useRef } from "react";
import { track } from "../utils/analytics";
import { PlaybookSource } from "../interfaces/documentCitationTypes";
import type { CitationTooltipGroup } from "./citationTooltipGroup";
import { wrapIndex } from "./citationTooltipGroup";

/** What the tooltip shows for one playbook citation. */
export interface PlaybookCitationTooltipData {
  documentTitle: string;
  breadcrumb: string[];
  importance: number | null;
  citedText: string;
  sources: PlaybookSource[];
}

interface PlaybookCitationTooltipState extends PlaybookCitationTooltipData {
  isVisible: boolean;
  anchorRect: DOMRect | null;
  /** Set when a merged chip is shown; null for a single citation. */
  group: CitationTooltipGroup<PlaybookCitationTooltipData> | null;
  /** Opened by click/Enter on a merged chip: ignores mouse leave until closed. */
  pinned: boolean;
}

interface PlaybookCitationTooltipContextType {
  state: PlaybookCitationTooltipState;
  showTooltip: (data: PlaybookCitationTooltipData & { anchorRect: DOMRect | null }) => void;
  /** Show a merged chip's citations, paged. Re-showing the same chip keeps its page. */
  showTooltipGroup: (
    anchorEl: HTMLElement,
    items: PlaybookCitationTooltipData[],
    pinned: boolean
  ) => void;
  /** Move the merged chip's tooltip by `delta` citations, wrapping around. */
  pageTooltip: (delta: number) => void;
  /** Hide now, pinned or not. */
  closeTooltip: () => void;
  hideTooltip: () => void;
  /** Keep tooltip visible when mouse enters tooltip area */
  handleTooltipMouseEnter: () => void;
  /** Hide tooltip when mouse leaves tooltip area */
  handleTooltipMouseLeave: () => void;
}

const defaultState: PlaybookCitationTooltipState = {
  isVisible: false,
  anchorRect: null,
  documentTitle: "",
  breadcrumb: [],
  importance: null,
  citedText: "",
  sources: [],
  group: null,
  pinned: false,
};

const PlaybookCitationTooltipContext = createContext<
  PlaybookCitationTooltipContextType | undefined
>(undefined);

interface PlaybookCitationTooltipProviderProps {
  children: React.ReactNode;
  scrollContainerRef?: React.RefObject<HTMLElement>;
}

/**
 * Provider for playbook citation tooltip state. Sibling of
 * DocumentCitationTooltipProvider — both mount once at the chat root so
 * tooltips never unmount during streaming.
 */
export const PlaybookCitationTooltipProvider: React.FC<PlaybookCitationTooltipProviderProps> = ({
  children,
  scrollContainerRef,
}) => {
  const [state, setState] = useState<PlaybookCitationTooltipState>(defaultState);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const analyticsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isVisibleRef = useRef(false);
  const pinnedRef = useRef(false);
  const groupRef = useRef<CitationTooltipGroup<PlaybookCitationTooltipData> | null>(null);

  const clearHideTimeout = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  const clearAnalyticsTimeout = useCallback(() => {
    if (analyticsTimeoutRef.current) {
      clearTimeout(analyticsTimeoutRef.current);
      analyticsTimeoutRef.current = null;
    }
  }, []);

  const showTooltip = useCallback(
    (data: PlaybookCitationTooltipData & { anchorRect: DOMRect | null }) => {
      clearHideTimeout();
      clearAnalyticsTimeout();
      isVisibleRef.current = true;
      pinnedRef.current = false;
      groupRef.current = null;
      setState({ isVisible: true, ...data, group: null, pinned: false });
      analyticsTimeoutRef.current = setTimeout(() => {
        track("citation:tooltip_viewed", {
          citation_type: "playbook",
          document_title: data.documentTitle,
          breadcrumb: data.breadcrumb.join(" › "),
          source_count: data.sources.length,
        });
        analyticsTimeoutRef.current = null;
      }, 1000);
    },
    [clearHideTimeout, clearAnalyticsTimeout]
  );

  // One view per page a reader dwells on, tagged with its place in the group.
  const trackGroupPageViewed = useCallback(
    (group: CitationTooltipGroup<PlaybookCitationTooltipData>) => {
      clearAnalyticsTimeout();
      const item = group.items[group.index];

      analyticsTimeoutRef.current = setTimeout(() => {
        track("citation:tooltip_viewed", {
          citation_type: "playbook",
          document_title: item.documentTitle,
          breadcrumb: item.breadcrumb.join(" › "),
          source_count: item.sources.length,
          group_size: group.items.length,
          group_index: group.index,
        });
        analyticsTimeoutRef.current = null;
      }, 1000);
    },
    [clearAnalyticsTimeout]
  );

  const showTooltipGroup = useCallback(
    (anchorEl: HTMLElement, items: PlaybookCitationTooltipData[], pinned: boolean) => {
      if (items.length === 0) return;
      clearHideTimeout();
      const current = groupRef.current;
      const sameChip = isVisibleRef.current && current?.anchorEl === anchorEl;
      const group = {
        items,
        index: sameChip && current ? Math.min(current.index, items.length - 1) : 0,
        anchorEl,
      };
      const nextPinned = pinned || (sameChip && pinnedRef.current);

      isVisibleRef.current = true;
      pinnedRef.current = nextPinned;
      groupRef.current = group;
      setState({
        isVisible: true,
        anchorRect: anchorEl.getBoundingClientRect(),
        ...items[group.index],
        group,
        pinned: nextPinned,
      });
      if (!sameChip) trackGroupPageViewed(group);
    },
    [clearHideTimeout, trackGroupPageViewed]
  );

  const pageTooltip = useCallback(
    (delta: number) => {
      const current = groupRef.current;

      if (!current || !isVisibleRef.current) return;
      const group = { ...current, index: wrapIndex(current.index + delta, current.items.length) };

      groupRef.current = group;
      setState((prev) => ({ ...prev, ...group.items[group.index], group }));
      trackGroupPageViewed(group);
    },
    [trackGroupPageViewed]
  );

  const closeTooltip = useCallback(() => {
    clearHideTimeout();
    clearAnalyticsTimeout();
    isVisibleRef.current = false;
    pinnedRef.current = false;
    setState((prev) => ({ ...prev, isVisible: false, pinned: false }));
  }, [clearHideTimeout, clearAnalyticsTimeout]);

  const hideTooltip = useCallback(() => {
    if (pinnedRef.current) return;
    clearAnalyticsTimeout();
    hideTimeoutRef.current = setTimeout(() => {
      isVisibleRef.current = false;
      setState((prev) => ({ ...prev, isVisible: false }));
    }, 100);
  }, [clearAnalyticsTimeout]);

  const handleTooltipMouseEnter = useCallback(() => {
    clearHideTimeout();
  }, [clearHideTimeout]);

  const handleTooltipMouseLeave = useCallback(() => {
    if (pinnedRef.current) return;
    // Clear any pending `hideTooltip` timer before flipping state — otherwise
    // the deferred timeout fires after we've already hidden and produces a
    // spurious re-render with a fresh state-object identity.
    clearHideTimeout();
    clearAnalyticsTimeout();
    isVisibleRef.current = false;
    setState((prev) => ({ ...prev, isVisible: false }));
  }, [clearHideTimeout, clearAnalyticsTimeout]);

  React.useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
      clearAnalyticsTimeout();
    };
  }, [clearAnalyticsTimeout]);

  React.useEffect(() => {
    const scrollContainer = scrollContainerRef?.current;
    if (!scrollContainer) return;

    const handleScroll = () => {
      if (!isVisibleRef.current) return;
      clearHideTimeout();
      clearAnalyticsTimeout();
      isVisibleRef.current = false;
      pinnedRef.current = false;
      setState((prev) => ({ ...prev, isVisible: false, pinned: false }));
    };

    scrollContainer.addEventListener("scroll", handleScroll, { passive: true });
    return () => scrollContainer.removeEventListener("scroll", handleScroll);
  }, [scrollContainerRef, clearHideTimeout, clearAnalyticsTimeout]);

  return (
    <PlaybookCitationTooltipContext.Provider
      value={{
        state,
        showTooltip,
        showTooltipGroup,
        pageTooltip,
        closeTooltip,
        hideTooltip,
        handleTooltipMouseEnter,
        handleTooltipMouseLeave,
      }}
    >
      {children}
    </PlaybookCitationTooltipContext.Provider>
  );
};

export const usePlaybookCitationTooltip = (): PlaybookCitationTooltipContextType => {
  const context = useContext(PlaybookCitationTooltipContext);
  if (!context) {
    throw new Error(
      "usePlaybookCitationTooltip must be used within PlaybookCitationTooltipProvider"
    );
  }
  return context;
};

export default PlaybookCitationTooltipContext;
